"use client"

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { getRequest, postRequest } from '../../Fetch/request';
import Loader from '../Loader/Loader';
import DiagramDraw from '../Common/DiagramDraw';
import EvaluationFeedbackPanel from '../Common/EvaluationFeedbackPanel';
import { getAIFeedbackSync } from '../../Utils/aiUtils';
// API evaluation removed for API diagrams
import { addLearningPointsToNotes } from '../../Utils/learningPointsUtils';
import { normalizeDiagramFeedback } from '../../Utils/diagramFeedbackUtils';
import { Box, Snackbar, Alert } from '@mui/material';

export default function SchemaDesigner({ problemId, schemaDesign, setSchemaDesign, componentType = 'LLD', documentVisible = false, setDocumentVisible = () => {}, hideEvaluate = false, hideViewDoc = false, onSelectionChange = null, deselectAllCallback = null, onCopy = null, clearCopiedCallback = null, onUnsavedChangesChange = () => {}, itemType = 'entity', functionalRequirements = [], scale = [], problemTitle = '' }) {
  const schema = schemaDesign || { entities: [], relationships: [] };
  
  const safeSchema = {
    entities: Array.isArray(schema.entities) ? schema.entities : [],
    relationships: Array.isArray(schema.relationships) ? schema.relationships : []
  };
  
  const [localSchema, setLocalSchema] = useState(safeSchema);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [saveStatus, setSaveStatus] = useState('idle');
  const [loading, setLoading] = useState(true);
  const [hasInitialized, setHasInitialized] = useState(false);
  const [hasLoadedFromLocalStorage, setHasLoadedFromLocalStorage] = useState(false);
  const [isEvaluating, setIsEvaluating] = useState(false);
  const [evaluationFeedback, setEvaluationFeedback] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const isEvaluatingRef = useRef(false); // Guard to prevent multiple simultaneous calls
  
  // Use refs to store latest values for cleanup
  const localSchemaRef = useRef(localSchema);
  const hasUnsavedChangesRef = useRef(hasUnsavedChanges);
  
  // Keep refs in sync with state
  useEffect(() => {
    localSchemaRef.current = localSchema;
    hasUnsavedChangesRef.current = hasUnsavedChanges;
  }, [localSchema, hasUnsavedChanges]);

  // Notify parent component about unsaved changes status
  useEffect(() => {
    onUnsavedChangesChange(hasUnsavedChanges);
  }, [hasUnsavedChanges, onUnsavedChangesChange]);

  // Create refs for relationships to avoid recreating callback
  const relationshipsRef = useRef([]);
  useEffect(() => {
    relationshipsRef.current = localSchema.relationships.map(rel => ({
      ...rel,
      fromEntityId: rel.fromEntityId || rel.from,
      toEntityId: rel.toEntityId || rel.to
    }));
  }, [localSchema.relationships]);

  // Save data before component unmounts (when switching steps)
  useEffect(() => {
    return () => {
      // Cleanup: Save data before unmounting using refs to get latest values
      if (problemId && problemId !== 'undefined' && problemId !== 'null') {
        const attemptKey = `${componentType.toLowerCase()}-attempt-${problemId}`;
        try {
          const currentAttemptData = JSON.parse(localStorage.getItem(attemptKey) || '{}');
          
          // Use refs to get the latest values (not stale closure values)
          const latestSchema = localSchemaRef.current;
          const latestHasUnsavedChanges = hasUnsavedChangesRef.current;
          
          // Store entities/APIs and relationships directly (flat structure)
          // Use different keys based on itemType
          if (itemType === 'api') {
            currentAttemptData.apiEntities = latestSchema.entities || [];
            currentAttemptData.apiRelationships = latestSchema.relationships || [];
            currentAttemptData.apiHasUnsavedChanges = latestHasUnsavedChanges;
          } else {
            currentAttemptData.schemaEntities = latestSchema.entities || [];
            currentAttemptData.schemaRelationships = latestSchema.relationships || [];
            currentAttemptData.schemaHasUnsavedChanges = latestHasUnsavedChanges;
          }
          currentAttemptData.timestamp = Date.now();
          localStorage.setItem(attemptKey, JSON.stringify(currentAttemptData));
        } catch (error) {
          console.error('Failed to save to localStorage on unmount:', error);
        }
      }
    };
  }, [problemId, componentType, itemType]);

  // Load schema design data from localStorage and backend - check every time component mounts
  useEffect(() => {
    if (!problemId) {
      setLoading(false);
      return;
    }

    const fetchData = async () => {
      try {
        // First, always check localStorage first
        const attemptKey = `${componentType.toLowerCase()}-attempt-${problemId}`;
        const attemptData = localStorage.getItem(attemptKey);
        
        if (attemptData) {
          try {
            const parsed = JSON.parse(attemptData);
            
            // Load from flat structure (not nested in schemaDesign)
            // Use different keys based on itemType
            let entitiesToLoad = null;
            if (itemType === 'api') {
              entitiesToLoad = parsed.apiEntities && Array.isArray(parsed.apiEntities) && parsed.apiEntities.length > 0
                ? parsed.apiEntities
                : null;
            } else {
              // Check both schemaEntities and entities fields (entities might be stored in either)
              entitiesToLoad = parsed.schemaEntities && Array.isArray(parsed.schemaEntities) && parsed.schemaEntities.length > 0
                ? parsed.schemaEntities
                : (parsed.entities && Array.isArray(parsed.entities) && parsed.entities.length > 0
                  ? parsed.entities
                  : null);
            }
            
            if (entitiesToLoad) {
              // Convert backend format (with fields array) to new format (with content string)
              const convertedEntities = entitiesToLoad.map(entity => {
                if (entity.content !== undefined) {
                  return { 
                    ...entity, 
                    content: entity.content || '',
                    headerColor: entity.headerColor || '#374151' // Ensure headerColor exists
                  };
                }
                
                // Convert fields array to content string
                if (entity.fields && Array.isArray(entity.fields)) {
                  const fieldsStr = entity.fields.map(field => {
                    if (typeof field === 'string') return field;
                    if (field && typeof field === 'object') {
                      return field.name || field.text || '';
                    }
                    return '';
                  }).filter(f => f.trim()).join('\n');
                  
                  return { 
                    ...entity, 
                    content: fieldsStr,
                    headerColor: entity.headerColor || '#374151' // Ensure headerColor exists
                  };
                }
                
                return { 
                  ...entity, 
                  content: '',
                  headerColor: entity.headerColor || '#374151' // Ensure headerColor exists
                };
              });
              
              // Load relationships based on itemType
              const relationshipsToLoad = itemType === 'api' 
                ? (parsed.apiRelationships || [])
                : (parsed.schemaRelationships || []);
              
              setLocalSchema({
                entities: convertedEntities,
                relationships: relationshipsToLoad
              });
              
              // Load unsaved changes flag based on itemType
              const unsavedChangesFlag = itemType === 'api'
                ? (parsed.apiHasUnsavedChanges || false)
                : (parsed.schemaHasUnsavedChanges || false);
              setHasUnsavedChanges(unsavedChangesFlag);
              setHasLoadedFromLocalStorage(true);
              
              // Also update localStorage to ensure proper keys are populated (for consistency)
              if (itemType === 'api') {
                if (!parsed.apiEntities || parsed.apiEntities.length === 0) {
                  try {
                    parsed.apiEntities = convertedEntities;
                    parsed.apiRelationships = relationshipsToLoad;
                    localStorage.setItem(attemptKey, JSON.stringify(parsed));
                  } catch (updateError) {
                    console.error('Failed to update apiEntities in localStorage:', updateError);
                  }
                }
              } else {
                // If we loaded from entities field, copy it to schemaEntities
                if (entitiesToLoad === parsed.entities && (!parsed.schemaEntities || parsed.schemaEntities.length === 0)) {
                  try {
                    parsed.schemaEntities = convertedEntities;
                    parsed.schemaRelationships = relationshipsToLoad;
                    localStorage.setItem(attemptKey, JSON.stringify(parsed));
                  } catch (updateError) {
                    console.error('Failed to update schemaEntities in localStorage:', updateError);
                  }
                }
              }
              
              // Data loaded from localStorage, no need to call backend
              setLoading(false);
              setHasInitialized(true);
              return;
            }
          } catch (localError) {
            console.error('Failed to parse attempt data:', localError);
          }
        }

        // Only fetch from backend if we don't have local data
        if (!hasLoadedFromLocalStorage) {
          try {
            const userId = localStorage.getItem('userId') || (JSON.parse(localStorage.getItem('profileObject') || '{}').id) || 'slazysloth_user';
            if (userId) {
              // Load from metadata endpoint based on componentType
              const endpoint = componentType === 'LLD'
                ? `${process.env.REACT_APP_URL}/lld-attempt?userId=${userId}&lldProblemId=${problemId}`
                : `${process.env.REACT_APP_URL}/hld-attempt?userId=${userId}&hldProblemId=${problemId}`;
              
              const attemptRes = await getRequest(endpoint);
              
              let backendSchema = null;
              if (componentType === 'LLD' && attemptRes?.status === '200' && attemptRes.response?.schemaDesign) {
                backendSchema = attemptRes.response.schemaDesign;
              } else if (componentType === 'HLD' && attemptRes?.status === '200' && attemptRes.response) {
                // HLD might store entities differently - check for schemaDesign or entities field
                backendSchema = attemptRes.response.schemaDesign || {
                  entities: attemptRes.response.entities || [],
                  relationships: []
                };
              }
              
              if (backendSchema) {
                // Convert backend format (with fields array) to new format (with content string)
                const convertedEntities = (backendSchema.entities || []).map(entity => {
                  // If entity is just a string, convert to object
                  if (typeof entity === 'string') {
                    return {
                      id: `entity_${Date.now()}_${Math.random()}`,
                      name: entity,
                      content: '',
                      position: { x: 100, y: 100 },
                      size: { width: 280, height: 280 },
                      headerColor: '#374151' // Default header color
                    };
                  }
                  
                  if (entity.content !== undefined) {
                    return { 
                      ...entity, 
                      content: entity.content || '',
                      headerColor: entity.headerColor || '#374151' // Ensure headerColor exists
                    };
                  }
                  
                  // Convert fields array to content string
                  if (entity.fields && Array.isArray(entity.fields)) {
                    const fieldsStr = entity.fields.map(field => {
                      if (typeof field === 'string') return field;
                      if (field && typeof field === 'object') {
                        return field.name || field.text || '';
                      }
                      return '';
                    }).filter(f => f.trim()).join('\n');
                    
                    return { 
                      ...entity, 
                      content: fieldsStr,
                      headerColor: entity.headerColor || '#374151' // Ensure headerColor exists
                    };
                  }
                  
                  return { 
                    ...entity, 
                    content: '',
                    headerColor: entity.headerColor || '#374151' // Ensure headerColor exists
                  };
                });
                
                // Store in flat structure (not nested in schemaDesign)
                const attemptKey = `${componentType.toLowerCase()}-attempt-${problemId}`;
                const currentAttemptData = JSON.parse(localStorage.getItem(attemptKey) || '{}');
                
                // Use different keys based on itemType
                if (itemType === 'api') {
                  currentAttemptData.apiEntities = convertedEntities;
                  currentAttemptData.apiRelationships = backendSchema.relationships || [];
                } else {
                  currentAttemptData.schemaEntities = convertedEntities;
                  currentAttemptData.schemaRelationships = backendSchema.relationships || [];
                }
                currentAttemptData.timestamp = Date.now();
                localStorage.setItem(attemptKey, JSON.stringify(currentAttemptData));
                
                setLocalSchema({
                  entities: convertedEntities,
                  relationships: backendSchema.relationships || []
                });
              }
            }
          } catch (backendError) {
            console.error('Failed to load schema design from backend:', backendError);
          }
        }
      } catch (error) {
        console.error('Error fetching schema design data:', error);
      } finally {
        setLoading(false);
        setHasInitialized(true);
      }
    };

    fetchData();
  }, [problemId, hasInitialized, hasLoadedFromLocalStorage, componentType, itemType]);

  // Update local schema when props change (but only if not loaded from backend/localStorage)
  // This is mainly for initial load from props, not for step switching
  useEffect(() => {
    if (hasInitialized && !hasLoadedFromLocalStorage && schemaDesign) {
      // Only update if we don't have local data
      const attemptKey = `${componentType.toLowerCase()}-attempt-${problemId}`;
      const existingData = JSON.parse(localStorage.getItem(attemptKey) || '{}');
      
      // If we already have local data, don't overwrite with props
      if (itemType === 'api') {
        if (existingData.apiEntities && existingData.apiEntities.length > 0) {
          return;
        }
      } else {
        if (existingData.schemaEntities && existingData.schemaEntities.length > 0) {
          return;
        }
      }
      
      // Convert old format (with fields array) to new format (with content string)
      const convertedEntities = safeSchema.entities.map(entity => {
        // If already has content field, use it
        if (entity.content !== undefined) {
          return { ...entity, content: entity.content || '' };
        }
        
        // Otherwise, convert fields array to content string
        if (entity.fields && Array.isArray(entity.fields)) {
          const fieldsStr = entity.fields.map(field => {
            if (typeof field === 'string') return field;
            if (field && typeof field === 'object') {
              return field.name || field.text || '';
            }
            return '';
          }).filter(f => f.trim()).join('\n');
          
          return { ...entity, content: fieldsStr };
        }
        
        return { ...entity, content: '' };
      });
      
      setLocalSchema({
        ...safeSchema,
        entities: convertedEntities
      });
      setHasUnsavedChanges(false);
          setSaveStatus('idle');
    }
  }, [schemaDesign, hasInitialized, hasLoadedFromLocalStorage, problemId, componentType]);

  // Save to localStorage - use main attempt key (like HLD) - flat structure
  useEffect(() => {
    if (problemId && problemId !== 'undefined' && problemId !== 'null' && hasInitialized) {
      const attemptKey = `${componentType.toLowerCase()}-attempt-${problemId}`;
      try {
        const currentAttemptData = JSON.parse(localStorage.getItem(attemptKey) || '{}');
        
        // Store entities/APIs and relationships directly (flat structure, not nested in schemaDesign)
        // Use different keys based on itemType
        if (itemType === 'api') {
          currentAttemptData.apiEntities = localSchema.entities || [];
          currentAttemptData.apiRelationships = localSchema.relationships || [];
          currentAttemptData.apiHasUnsavedChanges = hasUnsavedChanges;
        } else {
          currentAttemptData.schemaEntities = localSchema.entities || [];
          currentAttemptData.schemaRelationships = localSchema.relationships || [];
          currentAttemptData.schemaHasUnsavedChanges = hasUnsavedChanges;
        }
        currentAttemptData.timestamp = Date.now();
        localStorage.setItem(attemptKey, JSON.stringify(currentAttemptData));
      } catch (error) {
        console.error('Failed to save to localStorage:', error);
      }
    }
  }, [localSchema, hasUnsavedChanges, problemId, hasInitialized, componentType, itemType]);

  // Memoize setItems and setConnections callbacks to prevent infinite loops
  const handleSetItems = useCallback((updater) => {
    if (typeof updater === 'function') {
      setLocalSchema(prev => ({ ...prev, entities: updater(prev.entities) }));
    } else {
      setLocalSchema(prev => ({ ...prev, entities: updater }));
    }
    setHasUnsavedChanges(true);
  }, []);

  const handleSetConnections = useCallback((updater) => {
    if (typeof updater === 'function') {
      setLocalSchema(prev => ({ ...prev, relationships: updater(prev.relationships) }));
    } else {
      setLocalSchema(prev => ({ ...prev, relationships: updater }));
    }
    setHasUnsavedChanges(true);
  }, []);

  const saveSchema = async () => {
    setSaveStatus('saving');
    try {
      const userId = localStorage.getItem('userId') || (JSON.parse(localStorage.getItem('profileObject') || '{}').id) || 'slazysloth_user';
      
      // Read latest data from main attempt localStorage key (flat structure)
      const attemptKey = `${componentType.toLowerCase()}-attempt-${problemId}`;
      const attemptData = JSON.parse(localStorage.getItem(attemptKey) || '{}');
      let entitiesToSave = localSchema.entities;
      let relationshipsToSave = localSchema.relationships;
      
      // Load from flat structure (not nested in schemaDesign)
      // Use different keys based on itemType
      const entitiesKey = itemType === 'api' ? 'apiEntities' : 'schemaEntities';
      const relationshipsKey = itemType === 'api' ? 'apiRelationships' : 'schemaRelationships';
      
      if (attemptData[entitiesKey] && Array.isArray(attemptData[entitiesKey])) {
        try {
          // Entities/APIs are already in frontend format (with content string)
          entitiesToSave = attemptData[entitiesKey];
          relationshipsToSave = attemptData[relationshipsKey] || [];
          setLocalSchema({
            entities: entitiesToSave,
            relationships: relationshipsToSave
          });
        } catch (error) {
          console.error('Failed to parse localStorage data:', error);
          // Fallback to current state
        }
      }
      
      // Convert content back to fields format for backend compatibility
      const schemaToSave = {
        entities: entitiesToSave.map(entity => {
          // Convert content string to fields array
          const fields = (entity.content || '').split('\n')
            .filter(line => line.trim())
            .map((line, index) => ({
              id: `field_${entity.id}_${index}`,
              name: line.trim(),
              type: 'string',
              keyType: 'none'
            }));
          
          return {
            ...entity,
            fields: fields.length > 0 ? fields : [{ id: `field_${entity.id}_0`, name: 'id', type: 'uuid', keyType: 'primary' }],
            headerColor: entity.headerColor || '#374151', // Preserve headerColor
            highlights: entity.highlights || [] // Preserve highlights
          };
        }),
        relationships: relationshipsToSave
      };
      
      // Save entities via update-drawing endpoint (same for both HLD and LLD)
      // IMPORTANT: Only send the field that was updated (entities)
      // Other metadata (non-functional requirements, APIs, deep dives) use update-metadata endpoint
      
      const endpoint = '/' + (componentType === 'LLD' ? 'lld' : 'hld') + '-attempt/update-drawing';
      
      if (componentType === 'LLD') {
        // Only send entities (not classDiagram)
        const requestBody = {
          userId: userId,
          lldProblemId: problemId,
          entities: schemaToSave.entities
        };
        
        await postRequest(process.env.REACT_APP_URL + endpoint, requestBody);
      } else {
        // HLD: Send entities or APIs based on itemType
        const requestBody = {
          userId: userId,
          hldProblemId: problemId
        };
        
        if (itemType === 'api') {
          // For APIs, save as apiEntities (if backend supports it) or as entities
          // For now, save as entities but we'll track it separately in localStorage
          requestBody.entities = schemaToSave.entities;
        } else {
          // For entities, save normally
          requestBody.entities = schemaToSave.entities;
        }
        
        await postRequest(process.env.REACT_APP_URL + endpoint, requestBody);
      }
      
      // Also update parent component if setSchemaDesign is provided
      if (setSchemaDesign) {
        await setSchemaDesign(schemaToSave);
      }
      
      setHasUnsavedChanges(false);
      setSaveStatus('success');
      // Update main attempt localStorage key to mark as saved (flat structure)
      // Preserve all existing data and only update entities/APIs and relationships
      const updatedAttemptData = {
        ...attemptData, // Preserve all existing data
      };
      
      // Update the appropriate keys based on itemType
      if (itemType === 'api') {
        updatedAttemptData.apiEntities = entitiesToSave;
        updatedAttemptData.apiRelationships = relationshipsToSave;
        updatedAttemptData.apiHasUnsavedChanges = false;
      } else {
        updatedAttemptData.schemaEntities = entitiesToSave;
        updatedAttemptData.schemaRelationships = relationshipsToSave;
        updatedAttemptData.schemaHasUnsavedChanges = false;
      }
      updatedAttemptData.timestamp = Date.now();
      localStorage.setItem(attemptKey, JSON.stringify(updatedAttemptData));
      
      const successMessage = itemType === 'api' ? 'APIs saved successfully!' : 'Entities saved successfully!';
      setSnackbar({ open: true, message: successMessage, severity: 'success' });
      setTimeout(() => setSaveStatus('idle'), 2000);
    } catch (error) {
      console.error('Save failed:', error);
      setSaveStatus('error');
      const errorMessage = itemType === 'api' ? 'Failed to save APIs. Please try again.' : 'Failed to save entities. Please try again.';
      setSnackbar({ open: true, message: errorMessage, severity: 'error' });
      setTimeout(() => setSaveStatus('idle'), 2000);
    }
  };

  // Convert relationships to use fromEntityId/toEntityId format
  const relationships = localSchema.relationships.map(rel => ({
    ...rel,
    fromEntityId: rel.fromEntityId || rel.from,
    toEntityId: rel.toEntityId || rel.to
  }));

  // Handle evaluation of all entities
  const handleEvaluate = useCallback(async () => {
    // Prevent multiple simultaneous calls
    if (isEvaluatingRef.current) {
      return;
    }

    // Get current schema at call time (not from closure) using refs
    const currentSchema = localSchemaRef.current || localSchema;
    const currentEntities = currentSchema.entities || [];
    const currentRelationships = relationshipsRef.current || [];

    if (!problemId || currentEntities.length === 0) {
      const warningMessage = itemType === 'api' 
        ? 'Please add at least one API before evaluating.' 
        : 'Please add at least one entity before evaluating.';
      setSnackbar({ open: true, message: warningMessage, severity: 'warning' });
      return;
    }

    // Evaluation disabled for API diagrams
    if (itemType === 'api') {
      alert('API diagram evaluation is not available.');
      return;
    }

    // Entity diagram evaluation is no longer available
    if (itemType !== 'api') {
      alert('Entity diagram evaluation is no longer available. Please use code evaluation instead.');
      return;
    }
  }, [problemId, componentType, itemType, problemTitle, functionalRequirements, scale, localSchema]); // Include all dependencies

  // Load evaluation feedback from sessionStorage on mount (session-only, clears on refresh)
  useEffect(() => {
    if (problemId && componentType) {
      const itemTypeKey = itemType === 'api' ? 'apis' : 'entities';
      const feedbackKey = `evaluation-feedback-${componentType.toLowerCase()}-${itemTypeKey}-${problemId}`;
      const savedFeedback = sessionStorage.getItem(feedbackKey);
      if (savedFeedback) {
        try {
          const parsed = JSON.parse(savedFeedback);
          if (
            parsed?.feedback?.overall_assessment?.toLowerCase().includes('failed to evaluate')
          ) {
            sessionStorage.removeItem(feedbackKey);
          } else {
            setEvaluationFeedback(parsed.feedback);
          }
        } catch (e) {
          sessionStorage.removeItem(feedbackKey);
        }
      }
    }
  }, [problemId, componentType, itemType]);

  if (loading) {
  return (
      <div style={{
        height: 'calc(100vh - 100px)',
          display: 'flex',
        justifyContent: 'center',
          alignItems: 'center',
        backgroundColor: '#fafafa'
      }}>
        <Loader />
      </div>
    );
  }

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ flex: 1, overflow: 'auto' }}>
        <DiagramDraw
          problemId={problemId}
          items={localSchema.entities}
          setItems={handleSetItems}
          connections={relationships}
          setConnections={handleSetConnections}
          itemType={itemType}
          componentType={componentType}
          onSave={saveSchema}
          saveStatus={saveStatus}
          hasUnsavedChanges={hasUnsavedChanges}
          setHasUnsavedChanges={setHasUnsavedChanges}
          documentVisible={documentVisible}
          setDocumentVisible={setDocumentVisible}
          onEvaluate={hideEvaluate ? null : handleEvaluate}
          isEvaluating={isEvaluating}
          hideViewDoc={hideViewDoc}
          onSelectionChange={onSelectionChange}
          deselectAllCallback={deselectAllCallback}
          onCopy={onCopy}
          clearCopiedCallback={clearCopiedCallback}
          onSaveSnapshot={(snapshot) => {
            if (!problemId) return;
            const attemptKey = `lld-attempt-${problemId}`;
            try {
              const current = JSON.parse(localStorage.getItem(attemptKey) || '{}');
              current.schemaSnapshot = snapshot;
              localStorage.setItem(attemptKey, JSON.stringify(current));
              window.dispatchEvent(new CustomEvent('lldDiagramSnapshotUpdated', { detail: { problemId } }));
            } catch (e) { /* quota exceeded */ }
          }}
        />
      </Box>
      {evaluationFeedback && (
        <EvaluationFeedbackPanel
          feedback={evaluationFeedback}
          problemId={problemId}
          problemType={componentType}
          onClose={() => {
            setEvaluationFeedback(null);
            // Clear from sessionStorage
            const itemTypeKey = itemType === 'api' ? 'apis' : 'entities';
            const feedbackKey = `evaluation-feedback-${componentType.toLowerCase()}-${itemTypeKey}-${problemId}`;
            sessionStorage.removeItem(feedbackKey);
          }}
        />
      )}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
